import React, { useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useMediaQuery } from '@material-ui/core';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Badge from '@material-ui/core/Badge';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Button from '@material-ui/core/Button';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import Typography from '@material-ui/core/Typography';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import Box from '@material-ui/core/Box';
import Link from '@material-ui/core/Link';
import ButtonBase from '@material-ui/core/ButtonBase';

import { Auth } from '../../helpers/Auth';
import { AppState } from '../../redux';
import { getAuthenticationState, getCartState } from '../../redux/selectors';
import { setShopMode } from '../../redux/actions/shopMode.actions';
import { IUser } from '../../types/strapi';
import { ShopMode } from '../../redux/types';

import lortoLogo from '../../assets/images/lorto-logo.png';
import { DefaultNavigation } from './DefaultNavigation';
import { MobileNavigation } from './MobileNavigation';
import { ShopModeDialog } from '../ShopModeDialog';

const useStyles = makeStyles((theme) => ({
  routesToolbar: {
    flex: 5,
    justifyContent: 'space-between',
    backgroundColor: theme.palette.primary.main,
  },
  userToolbar: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: '#cddddf',
    color: theme.palette.primary.main,
  },
  userToolbarBox: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    '& > *:not(:last-child)': {
      marginRight: '20px',
      '&::after': {
        content: '"|"',
        left: '10px',
        position: 'relative',
      },
    },
    '& .MuiSvgIcon-root': {
      marginRight: '5px',
    },
  },
  userBox: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  logo: {
    background: `url(${lortoLogo}) center/contain no-repeat`,
    width: '120px',
    height: '50px',
  },
  userLink: {
    fontSize: '15px',
    fontWeight: 'normal',
  },
  cartBox: {
    display: 'flex',
    alignItems: 'center',
    '& .MuiBadge-root': {
      width: '20px',
      height: '0px',
      left: '-10px',
      marginLeft: '5px',
    },
  },
}));

interface TopBarProps {
  user: IUser | null;
  numberOfProducts: number;
  numberOfDirectSaleProducts: number;
  shopMode: ShopMode | null;
  setShopMode: (mode: ShopMode) => void;
}

const TopBar: React.FC<TopBarProps> = ({
  user,
  numberOfProducts,
  numberOfDirectSaleProducts,
  shopMode,
  setShopMode,
}) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const mediaQueryMatchesXs = useMediaQuery(theme.breakpoints.only('xs'));

  const currentModeCartSize = useMemo(
    () => (shopMode === ShopMode.LocalDelivery ? numberOfProducts : numberOfDirectSaleProducts),
    [shopMode, numberOfProducts, numberOfDirectSaleProducts]
  );

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = () => {
    setAnchorEl(null);
    Auth.signOut();
    navigate('/');
  };

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleConfirmMode = (mode: ShopMode) => {
    setShopMode(mode);
    setDialogOpen(false);
  };

  return (
    <AppBar position="relative" color="transparent">
      <Toolbar className={classes.routesToolbar} color="primary">
        <Box>
          <RouterLink to="/">
            <div className={classes.logo} />
          </RouterLink>
        </Box>
        {mediaQueryMatchesXs ? <MobileNavigation /> : <DefaultNavigation />}
      </Toolbar>
      <Toolbar className={classes.userToolbar} variant={'dense'}>
        <Box className={classes.userToolbarBox}>
          {user ? (
            <Box
              flexDirection={'row'}
              display={'flex'}
              alignItems={'center'}
              justifyContent={'flex-end'}
              data-testid="logged-in-user"
            >
              <ButtonBase className={classes.userBox} onClick={handleClick}>
                <AccountCircleIcon />
                <Typography variant="body1">{`${user.name} ${user.surname}`}</Typography>
              </ButtonBase>
              <Menu id="simple-menu" anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleClose}>
                <MenuItem onClick={handleSignOut}>Logout</MenuItem>
              </Menu>
            </Box>
          ) : (
            <>
              <Link className={classes.userLink} color="primary" underline="none" component={RouterLink} to="/signin">
                Accedi
              </Link>
              <Link className={classes.userLink} color="primary" underline="none" component={RouterLink} to="/signup">
                Registrati
              </Link>
            </>
          )}
          {user && user.role.type === 'owner' ? (
            <>
              <Link className={classes.userLink} component={Link} href={`${process.env.REACT_APP_BACKEND_URL}/admin`}>
                Gestione prodotti
              </Link>
              <Link className={classes.userLink} component={RouterLink} to="/orders">
                Ordini
              </Link>
              <Link className={classes.userLink} component={RouterLink} to="/online-orders">
                Ordini Online
              </Link>
            </>
          ) : (
            <>
              <Box>
                <Button variant="outlined" onClick={handleOpenDialog}>
                  <Typography variant="body2">
                    {shopMode === ShopMode.LocalDelivery ? 'Consegna diretta' : 'Vendita Online'}
                  </Typography>
                </Button>
              </Box>

              <ButtonBase className={classes.cartBox} onClick={() => navigate('/cart')} data-testid="cart-button">
                <ShoppingCartIcon fontSize="small" />
                <Typography variant="body1">Carrello</Typography>
                {currentModeCartSize > 0 && (
                  <Badge
                    color="secondary"
                    badgeContent={currentModeCartSize}
                    overlap="rectangular"
                    data-testid="cart-badge"
                  />
                )}
              </ButtonBase>
            </>
          )}
        </Box>
      </Toolbar>

      <ShopModeDialog open={dialogOpen} onConfirm={handleConfirmMode} />
    </AppBar>
  );
};

const mapStateToProps = (state: AppState) => ({
  user: getAuthenticationState(state).user,
  numberOfProducts: getCartState(state).items.length,
  numberOfDirectSaleProducts: state.directSaleCart.items.length,
  shopMode: state.shopMode.mode,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  setShopMode: (mode: ShopMode) => dispatch(setShopMode(mode)),
});

export default connect(mapStateToProps, mapDispatchToProps)(TopBar);
